/**
 * Eyewear Configurator
 * ES6 Class Architecture
 */
class EyewearConfigurator {
  constructor() {
    // 1. DOM Caching & Selectors
    this.modal = document.getElementById('eyewear-configurator-modal');
    if (!this.modal) return;

    this.triggers = document.querySelectorAll('[data-configurator-trigger]');
    this.closeBtns = document.querySelectorAll('[data-configurator-close]');
    this.lensCards = document.querySelectorAll('.eyewear-configurator__lens-card');
    this.colorCards = document.querySelectorAll('.eyewear-configurator__color-card');

    this.nextToStep2Btn = document.getElementById('NextToStep2');
    this.nextToStep3Btn = document.getElementById('NextToStep3');
    this.backBtn = document.getElementById('BackEyewearConfigurator');
    this.atcBtn = document.getElementById('configurator-add-to-cart');

    this.totalPriceEl = document.getElementById('EyewearConfiguratorTotalPrice');
    this.progressText = document.getElementById('EyewearConfiguratorProgressText');
    this.progressFill = document.getElementById('EyewearConfiguratorProgressFill');
    this.stepPanes = document.querySelectorAll('.eyewear-configurator__step-pane');

    this.summaryFrameName = document.getElementById('SummaryFrameName');
    this.summaryFramePrice = document.getElementById('SummaryFramePrice');
    this.summaryLensName = document.getElementById('SummaryLensName');
    this.summaryLensPrice = document.getElementById('SummaryLensPrice');
    this.summaryLensActionWrapper = document.getElementById('SummaryLensActionWrapper');
    this.summaryLensPricePersonalised = document.getElementById('SummaryLensPricePersonalised');
    this.summaryColorRow = document.getElementById('SummaryColorRow');
    this.summaryColorName = document.getElementById('SummaryColorName');
    this.editColorBtn = document.getElementById('EditColorSelection');
    this.editLensColorBtn = document.getElementById('EditLensColorSelection');

    this.summaryPrescriptionRow = document.getElementById('SummaryPrescriptionRow');
    this.summaryPrescriptionName = document.getElementById('SummaryPrescriptionName');
    this.editPrescriptionBtn = document.getElementById('EditPrescriptionSelection');

    // Prescription flow elements
    this.prescriptionPane = document.getElementById('PrescriptionSelectionPane');
    this.nextFromPrescriptionBtn = document.getElementById('NextFromPrescription');
    this.prescriptionOptionCards = document.querySelectorAll('[data-prescription-handle]');
    this.prescriptionLensCard = Array.from(this.lensCards).find(c => c.dataset && c.dataset.productHandle === 'prescription-lenses') || null;
    this.selectedPrescriptionOption = null;

    this.prescriptionOptionsPane = document.getElementById('PrescriptionAdditionalOptionsPane');
    this.nextFromPrescriptionOptionsBtn = document.getElementById('NextFromPrescriptionOptions');
    this.prescriptionAdditionalOptionCards = document.querySelectorAll('[data-prescription-option]');

    // Prescription upload pane elements (Phase 8.1)
    this.uploadPane = document.getElementById('PrescriptionUploadPane');
    this.uploadZone = document.getElementById('PrescriptionUploadZone');
    this.fileInput = document.getElementById('PrescriptionFileInput');
    this.fileCard = document.getElementById('PrescriptionFileCard');
    this.fileNameEl = document.getElementById('PrescriptionFileName');
    this.fileSizeEl = document.getElementById('PrescriptionFileSize');
    this.removeFileBtn = document.getElementById('RemovePrescriptionFileBtn');
    this.uploadErrorEl = document.getElementById('PrescriptionUploadError');
    this.validateUploadBtn = document.getElementById('ValidatePrescriptionUploadBtn');
    this.uploadFallbackBtn = document.getElementById('UploadFallbackSendLaterBtn');

    // Image Caching
    this.mainImageEl = document.getElementById('EyewearConfiguratorImage');
    this.originalImageSrc = this.mainImageEl ? this.mainImageEl.src : '';

    // State
    this.state = {
      frameVariantId: null,
      lensVariantId: null,
      bundleId: null,
      flowType: null,
      prescriptionAdditionalOption: null,
      prescriptionMethod: null
    };
    this.uploadedFile = null;
    this.currentStep = 1;
    this.scrollPosition = 0;
    this.previouslyFocusedEl = null;
    this.isEditing = false;
    this.isRestoringState = false;

    // Convert raw frame price cents to decimal
    const rawFramePrice = this.totalPriceEl ? this.totalPriceEl.dataset.basePrice : 0;
    this.framePrice = parseFloat(rawFramePrice) / 100;
    this.lensPrice = 0;

    this.selectedLens = null;
    this.selectedColor = null;

    this.basePrescriptionPrice = 0;
    if (this.prescriptionLensCard) {
      this.basePrescriptionPrice = parseFloat(this.prescriptionLensCard.dataset.basePrice || 0) / 100;
    }

    this.isRestoringEdit = false;
    this.initEvents();
  }

  /**
   * Bind Events
   */
  initEvents() {
    // Modal open/close listeners
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal();
      });
    });

    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeModal();
      });
    });

    // Step navigation buttons
    if (this.nextToStep2Btn) {
      this.nextToStep2Btn.addEventListener('click', () => {
        // Prescription lens selected → open prescription selection pane
        if (this.selectedLens && this.selectedLens.dataset.productHandle === 'prescription-lenses') {
          this.showPrescriptionPane();
        } else {
          // Polarised / Personalised → existing flow unchanged
          this.goToStep(2);
        }
      });
    }

    if (this.nextToStep3Btn) {
      this.nextToStep3Btn.addEventListener('click', () => {
        this.goToStep(3);
      });
    }

    if (this.backBtn) {
      this.backBtn.addEventListener('click', (e) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        // Double-click protection
        const now = Date.now();
        if (this.lastBackClick && (now - this.lastBackClick < 300)) {
          return;
        }
        this.lastBackClick = now;

        const isPrescription = this.selectedLens && this.selectedLens.dataset.productHandle === 'prescription-lenses';
        if (this.currentStep === 3 && isPrescription) {
          this.currentStep = 1;
          this.showPrescriptionOptionsPane();
        } else if (this.currentStep === 2 && isPrescription) {
          this.currentStep = 1;
          this.showPrescriptionOptionsPane();
        } else if (this.uploadPane && this.uploadPane.style.display === 'flex') {
          this.hidePrescriptionUploadPane();
        } else if (this.prescriptionOptionsPane && this.prescriptionOptionsPane.style.display === 'flex') {
          this.hidePrescriptionOptionsPane();
        } else if (this.prescriptionPane && this.prescriptionPane.style.display === 'flex') {
          this.hidePrescriptionPane();
        } else if (this.currentStep === 2) {
          this.resetConfigurator();
        } else if (this.currentStep > 1) {
          this.goToStep(this.currentStep - 1);
        }
      });
    }

    if (this.editColorBtn) {
      this.editColorBtn.addEventListener('click', () => {
        this.goToStep(2);
      });
    }

    if (this.editLensColorBtn) {
      this.editLensColorBtn.addEventListener('click', () => {
        const isPrescription = this.selectedLens && this.selectedLens.dataset.productHandle === 'prescription-lenses';
        if (isPrescription) {
          this.stepPanes.forEach(pane => { pane.style.display = 'none'; });
          this.showPrescriptionOptionsPane();
        } else {
          this.goToStep(1);
        }
      });
    }

    if (this.editPrescriptionBtn) {
      this.editPrescriptionBtn.addEventListener('click', () => {
        this.stepPanes.forEach(pane => { pane.style.display = 'none'; });
        this.showPrescriptionPane(true);
      });
    }

    // Card selection
    this.initCardSelection();

    // Prescription option cards selection (inside prescription pane)
    this.prescriptionOptionCards.forEach(card => {
      card.addEventListener('click', () => {
        this.prescriptionOptionCards.forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        this.selectedPrescriptionOption = card;

        // Save method in state
        const handle = card.dataset.prescriptionHandle;
        if (handle === 'prescription-send-later') {
          this.state.prescriptionMethod = 'send_later';
        } else if (handle === 'prescription-upload') {
          this.state.prescriptionMethod = 'upload_now';
        } else if (handle === 'prescription-fill-correction') {
          this.state.prescriptionMethod = 'fill_correction';
        } else {
          this.state.prescriptionMethod = null;
        }

        // Restore prescription lens card selection
        if (this.prescriptionLensCard) {
          this.prescriptionLensCard.classList.add('is-selected');
          this.selectedLens = this.prescriptionLensCard;
          this.lensPrice = parseFloat(this.prescriptionLensCard.dataset.basePrice || 0) / 100;
        }
        // Enable prescription Next button
        if (this.nextFromPrescriptionBtn) this.nextFromPrescriptionBtn.disabled = false;

        // Update footer total
        this.updateFooterTotal();
      });
    });

    // Prescription lens card (Step 1) → card click only selects (same as polarised/personalised)
    // Navigation happens via NEXT button above (not here)

    // Prescription pane Next button → proceed to upload pane or additional options
    if (this.nextFromPrescriptionBtn) {
      this.nextFromPrescriptionBtn.addEventListener('click', () => {
        if (this.selectedPrescriptionOption) {
          const handle = this.selectedPrescriptionOption.dataset.prescriptionHandle;
          if (handle === 'prescription-upload') {
            this.showPrescriptionUploadPane();
          } else {
            if (this.prescriptionPane) this.prescriptionPane.style.display = 'none';
            if (handle === 'prescription-send-later') {
              this.showPrescriptionOptionsPane();
            } else {
              this.goToStep(3);
            }
          }
        }
      });
    }

    // Removed duplicate event listeners to prevent race conditions on back navigation

    // Prescription additional options selection
    this.prescriptionAdditionalOptionCards.forEach(card => {
      card.addEventListener('click', () => {
        this.prescriptionAdditionalOptionCards.forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        
        this.state.prescriptionAdditionalOption = card.dataset.prescriptionOption;

        // Instant price update on option selection
        const selectedOptionPrice = parseFloat(card.dataset.price || 0) / 100;
        this.lensPrice = this.basePrescriptionPrice + selectedOptionPrice;
        this.updateFooterTotal();

        if (this.nextFromPrescriptionOptionsBtn) {
          this.nextFromPrescriptionOptionsBtn.disabled = false;
        }
      });
    });

    // Next button for prescription additional options
    if (this.nextFromPrescriptionOptionsBtn) {
      this.nextFromPrescriptionOptionsBtn.addEventListener('click', () => {
        if (this.state.prescriptionAdditionalOption === 'polarised' || this.state.prescriptionAdditionalOption === 'personalised') {
          this.goToStep(2);
        } else {
          this.goToStep(3);
        }
      });
    }

    // Add to Cart ajax
    if (this.atcBtn) {
      this.atcBtn.addEventListener('click', () => {
        this.addToCart();
      });
    }

    // Delegated click listener for editing eyewear bundles in cart drawer
    document.addEventListener('click', (e) => {
      const editBtn = e.target.closest('.eyewear-bundle-edit-btn');
      if (editBtn) {
        e.preventDefault();
        const bundleId = editBtn.dataset.bundleId;
        const frameVariantId = editBtn.dataset.frameVariantId;
        const lensVariantId = editBtn.dataset.lensVariantId;

        // Close cart drawer if open
        const cartDrawer = document.querySelector("m-cart-drawer");
        if (cartDrawer) {
          cartDrawer.close();
        }

        // Open configurator for edit
        this.openForEdit(bundleId, frameVariantId, lensVariantId, editBtn.dataset);
      }
    });

    // Resize listener: re-equalise prescription card heights when any pane is visible
    let _rxRaf = null;
    window.addEventListener('resize', () => {
      const isPaneVisible = (this.prescriptionPane && this.prescriptionPane.style.display !== 'none') || 
                            (this.uploadPane && this.uploadPane.style.display !== 'none') ||
                            (this.prescriptionOptionsPane && this.prescriptionOptionsPane.style.display !== 'none');
      if (!isPaneVisible) return;
      cancelAnimationFrame(_rxRaf);
      _rxRaf = requestAnimationFrame(() => this.equalizePrescriptionCardHeights());
    });

    this.initUploadEvents();
  }

  /**
   * Card Selection Logic
   */
  initCardSelection() {
    // Lens selection (Step 1)
    this.lensCards.forEach(card => {
      card.addEventListener('click', (e) => {
        // Bypass step 1 lens selection logic if this is a card inside the prescription panes
        if (card.closest('#PrescriptionSelectionPane') || card.closest('#PrescriptionAdditionalOptionsPane')) {
          return;
        }

        // Before ANY lens card selection:
        if (card.dataset.enabled !== 'true') {
          if (e) e.preventDefault();
          return;
        }

        this.lensCards.forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        this.selectedLens = card;

        // Update main image if data-image-url exists
        if (this.mainImageEl && card.dataset.imageUrl) {
          this.mainImageEl.src = card.dataset.imageUrl;
        }

        // Fetch price in cents and convert to decimal
        const rawPrice = card.dataset.basePrice || card.dataset.price || 0;
        this.lensPrice = parseFloat(rawPrice) / 100;

        // Update flowType in state
        const handle = card.dataset.productHandle;
        if (handle === 'personalised-lens-colour') {
          this.state.flowType = 'personalised';
        } else if (handle === 'polarised-lenses') {
          this.state.flowType = 'polarised';
        } else {
          this.state.flowType = null;
        }

        // Enable next step button
        if (this.nextToStep2Btn) {
          this.nextToStep2Btn.disabled = false;
        }

        this.updateFooterTotal();
      });
    });

    // Color selection (Step 2)
    this.colorCards.forEach(card => {
      card.addEventListener('click', () => {
        this.colorCards.forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        this.selectedColor = card;

        const isPrescription = this.selectedLens && this.selectedLens.dataset.productHandle === 'prescription-lenses';
        if (!isPrescription) {
          // Fetch price in cents and convert to decimal
          const rawPrice = card.dataset.price || 0;
          this.lensPrice = parseFloat(rawPrice) / 100;
        }

        // Update swatch selected name labels
        const gradientLabel = document.getElementById('PersonalisedGradientSelectedName');
        const solidLabel = document.getElementById('PersonalisedSolidSelectedName');
        if (gradientLabel) gradientLabel.textContent = '';
        if (solidLabel) solidLabel.textContent = '';

        if (card.classList.contains('swatch-card')) {
          const groupHeader = card.closest('.eyewear-configurator__swatch-group');
          const groupLabel = groupHeader ? groupHeader.querySelector('.eyewear-configurator__swatch-group-label') : null;
          const isGradient = groupLabel ? groupLabel.textContent.includes('GRADIENT') : false;
          const name = card.dataset.name || '';
          if (isGradient) {
            if (gradientLabel) gradientLabel.textContent = name;
          } else {
            if (solidLabel) solidLabel.textContent = name;
          }
        }

        // Enable next step button
        if (this.nextToStep3Btn) {
          this.nextToStep3Btn.disabled = false;
        }

        this.updateFooterTotal();
      });
    });
  }

  /**
   * Open Modal
   */
  openModal() {
    this.previouslyFocusedEl = document.activeElement;
    this.modal.classList.add('is-open');
    this.lockScroll();
    this.resetConfigurator();

    // Attach keydown listener for Esc and Focus Trap
    this.keydownHandler = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.keydownHandler);

    // Initial focus on close button or modal container
    const focusable = this.getFocusableElements();
    if (focusable.length > 0) {
      focusable[0].focus();
    } else {
      this.modal.focus();
    }
  }

  /**
   * Open Modal for Edit Mode
   */
  async openForEdit(bundleId, frameVariantId, lensVariantId, dataset = null) {
    this.isRestoringEdit = true;
    this.modal.classList.add('no-transition');
    this.modal.classList.add('is-loading');

    this.previouslyFocusedEl = document.activeElement;
    this.modal.classList.add('is-open');
    this.lockScroll();

    // Reset configurator state but do not clear editing state yet
    this.currentStep = 1;
    this.lensCards.forEach(card => card.classList.remove('is-selected'));
    this.colorCards.forEach(card => card.classList.remove('is-selected'));
    this.prescriptionOptionCards.forEach(card => card.classList.remove('is-selected'));
    this.prescriptionAdditionalOptionCards.forEach(card => card.classList.remove('is-selected'));
    this.selectedLens = null;
    this.selectedColor = null;
    this.selectedPrescriptionOption = null;
    this.state.prescriptionMethod = null;
    this.state.prescriptionAdditionalOption = null;
    this.state.flowType = null;
    this.lensPrice = 0;
    this.resetUploadState();
    if (this.mainImageEl && this.originalImageSrc) {
      this.mainImageEl.src = this.originalImageSrc;
    }
    if (this.nextToStep2Btn) this.nextToStep2Btn.disabled = true;
    if (this.nextToStep3Btn) this.nextToStep3Btn.disabled = true;
    if (this.summaryColorRow) {
      this.summaryColorRow.style.display = 'none';
    }

    // Set editing state
    this.editingBundleId = bundleId;
    this.isEditing = true;

    // Attach keydown listener for Esc and Focus Trap
    this.keydownHandler = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.keydownHandler);

    // Fast Path Check
    const hasInstantMetadata = dataset && dataset.bundleId && dataset.flowType && dataset.lensType;

    let isPrescription = false;
    let prescriptionMethod = null;
    let additionalOption = null;
    let selectedColorVal = null;
    let selectedProductHandle = 'polarised-lenses';

    if (hasInstantMetadata) {
      // Fast Path
      isPrescription = (dataset.lensType === 'prescription-lenses');
      if (isPrescription) {
        prescriptionMethod = dataset.prescriptionMethod || 'send_later';
        additionalOption = dataset.additionalOption || 'none';
        selectedColorVal = dataset.selectedColor || dataset.lensColor || null;
      } else {
        selectedProductHandle = dataset.lensType;
        selectedColorVal = dataset.selectedColor || dataset.lensColor || null;
      }

      // Restore lens price temporarily from dataset (will be recalculated later)
      const rawLensPrice = parseFloat(dataset.lensPrice || 0) / 100;
      this.lensPrice = rawLensPrice;

    } else {
      // Legacy Fallback Path: Show loader and fetch /cart.js
      this.isRestoringState = true;
      this.modal.classList.add('is-loading');

      let cart = null;
      try {
        const response = await fetch((window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart.js');
        cart = await response.json();
      } catch (err) {
        console.error("Failed to fetch cart for edit routing", err);
      }

      const bundleItems = cart ? cart.items.filter(item => item.properties && String(item.properties._bundle_id) === String(bundleId)) : [];
      const lensItem = bundleItems.find(item => item.properties._is_lens === 'true');
      const colorAddonItem = bundleItems.find(item => item.properties._is_color_addon === 'true');

      if (lensItem) {
        isPrescription = (lensItem.properties._lens_type === 'prescription-lenses') || (lensItem.handle === 'prescription-lenses');
        if (isPrescription) {
          prescriptionMethod = lensItem.properties._prescription_method || 'send_later';
          additionalOption = lensItem.properties._additional_option || 'none';
          selectedColorVal = lensItem.properties._selected_color || lensItem.properties._lens_color || null;
        } else {
          selectedProductHandle = lensItem.handle || 'polarised-lenses';
          selectedColorVal = lensItem.properties._selected_color || lensItem.properties._lens_color || null;
        }
      } else {
        // Fallback: match by variant ID if no lensItem found
        const prescriptionLensCard = this.prescriptionLensCard;
        if (prescriptionLensCard && String(prescriptionLensCard.dataset.variantId).trim() === String(lensVariantId).trim()) {
          isPrescription = true;
          prescriptionMethod = 'send_later';
          additionalOption = 'none';
        }
      }

      if (isPrescription && colorAddonItem) {
        if (!selectedColorVal) {
          selectedColorVal = colorAddonItem.properties._lens_color || null;
        }
        if (additionalOption === 'none') {
          const variantId = colorAddonItem.variant_id;
          const colorCard = Array.from(this.colorCards).find(c => String(c.dataset.variantId).trim() === String(variantId).trim());
          if (colorCard && (colorCard.classList.contains('swatch-card') || colorCard.closest('.eyewear-configurator__color-options--personalised'))) {
            additionalOption = 'personalised';
          } else {
            additionalOption = 'polarised';
          }
        }
      }
    }

    // Restore Selections and UI Cards
    if (isPrescription) {
      // 1. Select Lens Card
      const lensCard = this.prescriptionLensCard || Array.from(this.lensCards).find(card => card.dataset.productHandle === 'prescription-lenses');
      if (lensCard) {
        lensCard.classList.add('is-selected');
        this.selectedLens = lensCard;
        if (this.mainImageEl && lensCard.dataset.imageUrl) {
          this.mainImageEl.src = lensCard.dataset.imageUrl;
        }
      }

      // 2. Select Prescription Method Card
      let methodHandle = 'prescription-send-later';
      if (prescriptionMethod === 'upload' || prescriptionMethod === 'upload_now') {
        methodHandle = 'prescription-upload';
      } else if (prescriptionMethod === 'fill_correction') {
        methodHandle = 'prescription-fill-correction';
      }
      const methodCard = Array.from(this.prescriptionOptionCards).find(c => c.dataset.prescriptionHandle === methodHandle);
      if (methodCard) {
        methodCard.classList.add('is-selected');
        this.selectedPrescriptionOption = methodCard;
        this.state.prescriptionMethod = prescriptionMethod;
      }

      // 3. Select Additional Option Card
      const optionCard = Array.from(this.prescriptionAdditionalOptionCards).find(c => c.dataset.prescriptionOption === additionalOption);
      if (optionCard) {
        optionCard.classList.add('is-selected');
        this.state.prescriptionAdditionalOption = additionalOption;
      }

      // 4. Select Color Card (if polarised or personalised option chosen)
      if (additionalOption === 'polarised' || additionalOption === 'personalised') {
        let colorCard = null;
        if (selectedColorVal) {
          colorCard = Array.from(this.colorCards).find(card => {
            const titleEl = card.querySelector('.eyewear-configurator__color-name');
            const name = titleEl ? titleEl.textContent.trim() : (card.dataset.name || '');
            return name.toLowerCase() === selectedColorVal.toLowerCase();
          });
        }
        if (colorCard) {
          colorCard.classList.add('is-selected');
          this.selectedColor = colorCard;
          if (this.mainImageEl && colorCard.dataset.imageUrl) {
            this.mainImageEl.src = colorCard.dataset.imageUrl;
          }
        }
      }

      // Recalculate lens price using normal configurator logic
      this.recalculateLensPrice();

      // Rebuild summary state
      this.buildSummary();

      // Open at correct sub-pane for prescription flows
      if (prescriptionMethod === 'upload_now' || prescriptionMethod === 'upload') {
        this.showPrescriptionUploadPane();
      } else {
        this.showPrescriptionPane(true);
      }

      // Finish restoration state, reveal UI, and clear flags
      this.modal.classList.remove('is-loading');
      this.modal.classList.remove('no-transition');
      this.isRestoringState = false;
      this.isRestoringEdit = false;

    } else {
      // Standard flow (polarised or personalised)
      if (selectedProductHandle === 'personalised-lens-colour') {
        this.state.flowType = 'personalised';
      } else {
        this.state.flowType = 'polarised';
      }

      // Select Lens Card
      const lensCard = Array.from(this.lensCards).find(card => card.dataset.productHandle === selectedProductHandle);
      if (lensCard) {
        lensCard.classList.add('is-selected');
        this.selectedLens = lensCard;
        if (this.mainImageEl && lensCard.dataset.imageUrl) {
          this.mainImageEl.src = lensCard.dataset.imageUrl;
        }
      }

      // Select Color Card
      let colorCard = null;
      if (!hasInstantMetadata) {
        colorCard = Array.from(this.colorCards).find(card => String(card.dataset.variantId).trim() === String(lensVariantId).trim());
      }
      if (!colorCard && selectedColorVal) {
        colorCard = Array.from(this.colorCards).find(card => {
          const titleEl = card.querySelector('.eyewear-configurator__color-name');
          const name = titleEl ? titleEl.textContent.trim() : (card.dataset.name || '');
          return name.toLowerCase() === selectedColorVal.toLowerCase();
        });
      }
      if (colorCard) {
        colorCard.classList.add('is-selected');
        this.selectedColor = colorCard;
        if (this.mainImageEl && colorCard.dataset.imageUrl) {
          this.mainImageEl.src = colorCard.dataset.imageUrl;
        }
      }

      // Recalculate lens price using normal configurator logic
      this.recalculateLensPrice();

      // Rebuild summary state
      this.buildSummary();

      // Open Step 3 (Summary Screen) for standard flow
      this.goToStep(3);

      // Finish restoration state, reveal UI, and clear flags
      this.modal.classList.remove('is-loading');
      this.modal.classList.remove('no-transition');
      this.isRestoringState = false;
      this.isRestoringEdit = false;
    }
  }

  /**
   * Close Modal
   */
  closeModal() {
    this.modal.classList.remove('is-open');
    this.unlockScroll();
    this.resetUploadState();
    if (this.uploadPane) {
      this.uploadPane.style.display = 'none';
    }

    // Detach keydown listener
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }

    // Restore focus
    if (this.previouslyFocusedEl) {
      this.previouslyFocusedEl.focus();
    }
  }

  /**
   * Reset Modal State
   */
  resetConfigurator() {
    this.currentStep = 1;
    this.editingBundleId = null;
    this.isEditing = false;
    this.isRestoringState = false;
    this.state.flowType = null;
    this.state.prescriptionAdditionalOption = null;
    this.state.prescriptionMethod = null;
    this.resetUploadState();

    if (this.prescriptionOptionsPane) {
      this.prescriptionOptionsPane.style.display = 'none';
    }
    if (this.uploadPane) {
      this.uploadPane.style.display = 'none';
    }
    this.prescriptionAdditionalOptionCards.forEach(c => c.classList.remove('is-selected'));
    if (this.nextFromPrescriptionOptionsBtn) {
      this.nextFromPrescriptionOptionsBtn.disabled = true;
    }

    // Reset card selections
    this.lensCards.forEach(card => card.classList.remove('is-selected'));
    this.colorCards.forEach(card => card.classList.remove('is-selected'));

    this.selectedLens = null;
    this.selectedColor = null;
    this.lensPrice = 0;

    // Clear personalised swatch labels
    const gradientLabel = document.getElementById('PersonalisedGradientSelectedName');
    const solidLabel = document.getElementById('PersonalisedSolidSelectedName');
    if (gradientLabel) gradientLabel.textContent = '';
    if (solidLabel) solidLabel.textContent = '';

    // Reset image to original frame
    if (this.mainImageEl && this.originalImageSrc) {
      this.mainImageEl.src = this.originalImageSrc;
    }

    // Disable next buttons
    if (this.nextToStep2Btn) this.nextToStep2Btn.disabled = true;
    if (this.nextToStep3Btn) this.nextToStep3Btn.disabled = true;

    // Reset summary color row
    if (this.summaryColorRow) {
      this.summaryColorRow.style.display = 'none';
    }

    // Navigate to step 1 panel
    this.goToStep(1);
  }

  /**
   * Navigate to Specific Step
   */
  goToStep(step) {
    if (this.prescriptionOptionsPane) this.prescriptionOptionsPane.style.display = 'none';
    if (this.uploadPane) this.uploadPane.style.display = 'none';

    if (step === 2 && this.selectedColor) {
      if (this.nextToStep3Btn) this.nextToStep3Btn.disabled = false;
    }

    // Reset color selections when navigating from Step 1 to Step 2
    if (step === 2 && this.currentStep === 1) {
      const isPrescription = this.selectedLens && this.selectedLens.dataset.productHandle === 'prescription-lenses';
      if (!isPrescription || !this.selectedColor) {
        this.colorCards.forEach(card => card.classList.remove('is-selected'));
        this.selectedColor = null;
        if (!isPrescription) {
          this.lensPrice = 0;
        }
        if (this.nextToStep3Btn) this.nextToStep3Btn.disabled = true;

        // Clear personalised swatch labels
        const gradientLabel = document.getElementById('PersonalisedGradientSelectedName');
        const solidLabel = document.getElementById('PersonalisedSolidSelectedName');
        if (gradientLabel) gradientLabel.textContent = '';
        if (solidLabel) solidLabel.textContent = '';
      }
    }

    this.currentStep = step;

    // Toggle panels
    this.stepPanes.forEach(pane => {
      pane.style.display = 'none';
    });

    const activePane = this.modal.querySelector(`.eyewear-configurator__step-pane[data-step="${this.currentStep}"]`);
    if (activePane) {
      activePane.style.display = 'flex';
    }

    // Dynamic Step 2 configurations
    if (this.currentStep === 2) {
      const selectedHandle = this.selectedLens ? this.selectedLens.dataset.productHandle : '';
      const headingEl = document.getElementById('EyewearConfiguratorStep2Heading');
      const polarisedWrap = this.modal.querySelector('.eyewear-configurator__color-options--polarised');
      const personalisedWrap = this.modal.querySelector('.eyewear-configurator__color-options--personalised');

      const isPrescription = selectedHandle === 'prescription-lenses';
      const showPersonalised = (selectedHandle === 'personalised-lens-colour') || (isPrescription && this.state.prescriptionAdditionalOption === 'personalised');

      if (showPersonalised) {
        if (headingEl) headingEl.textContent = 'Select lens colour (+30 EUR)';
        if (polarisedWrap) polarisedWrap.style.display = 'none';
        if (personalisedWrap) personalisedWrap.style.display = 'block';
      } else {
        if (headingEl) headingEl.textContent = 'Select your polarized color tint';
        if (polarisedWrap) polarisedWrap.style.display = 'flex';
        if (personalisedWrap) personalisedWrap.style.display = 'none';
      }
    }

    // Update Progress Bar
    if (this.progressText) {
      this.progressText.textContent = `Step ${this.currentStep} of 3`;
    }
    if (this.progressFill) {
      this.progressFill.style.width = `${(this.currentStep / 3) * 100}%`;
    }

    // Toggle navigation button visibility
    if (this.backBtn) {
      this.backBtn.style.display = this.currentStep > 1 ? 'flex' : 'none';
    }

    if (this.atcBtn) {
      this.atcBtn.style.display = this.currentStep === 3 ? 'block' : 'none';
    }

    if (this.nextToStep2Btn) {
      this.nextToStep2Btn.style.display = this.currentStep === 1 ? 'block' : 'none';
    }

    if (this.nextToStep3Btn) {
      this.nextToStep3Btn.style.display = this.currentStep === 2 ? 'block' : 'none';
    }

    // Step 3 Summary compilation
    if (this.currentStep === 3) {
      this.buildSummary();
    }

    // Update footer total
    this.updateFooterTotal();
  }

  /**
   * Footer Price Calculation
   */
  updateFooterTotal() {
    if (!this.totalPriceEl) return;

    let total = this.framePrice;
    const isPrescription = this.selectedLens && this.selectedLens.dataset.productHandle === 'prescription-lenses';
    if (isPrescription) {
      total += this.lensPrice;
    } else {
      if (this.currentStep > 1 && this.selectedColor) {
        total += this.lensPrice;
      }
    }

    this.totalPriceEl.innerHTML = this.formatMoney(total * 100);
  }

  /**
   * Recalculate Lens Price dynamically
   */
  recalculateLensPrice() {
    const isPrescription = this.selectedLens && this.selectedLens.dataset.productHandle === 'prescription-lenses';
    if (isPrescription) {
      const optionCard = Array.from(this.prescriptionAdditionalOptionCards).find(c => c.classList.contains('is-selected'));
      const selectedOptionPrice = optionCard ? parseFloat(optionCard.dataset.price || 0) / 100 : 0;
      this.lensPrice = this.basePrescriptionPrice + selectedOptionPrice;
    } else {
      if (this.selectedColor) {
        this.lensPrice = parseFloat(this.selectedColor.dataset.price || 0) / 100;
      } else if (this.selectedLens) {
        const rawPrice = this.selectedLens.dataset.basePrice || this.selectedLens.dataset.price || 0;
        this.lensPrice = parseFloat(rawPrice) / 100;
      } else {
        this.lensPrice = 0;
      }
    }
    this.updateFooterTotal();
  }

  /**
   * Build Summary Panel (Step 3)
   */
  buildSummary() {
    if (this.summaryPrescriptionRow) {
      this.summaryPrescriptionRow.style.display = 'none';
    }

    const selectedHandle = this.selectedLens ? this.selectedLens.dataset.productHandle : '';
    const isPersonalised = selectedHandle === 'personalised-lens-colour';

    if (isPersonalised) {
      if (this.summaryFramePrice) {
        this.summaryFramePrice.textContent = this.formatMoney(this.framePrice * 100).replace(/^\+\s*/, '');
      }
      if (this.summaryLensName) {
        this.summaryLensName.textContent = 'Standard lenses';
      }
      // Personalised: hide bare price, show action wrapper (Complimentary + Edit)
      if (this.summaryLensPrice) this.summaryLensPrice.style.display = 'none';
      if (this.summaryLensActionWrapper) this.summaryLensActionWrapper.style.display = '';
      if (this.summaryLensPricePersonalised) {
        this.summaryLensPricePersonalised.textContent = 'Complimentary';
      }
      if (this.selectedColor) {
        if (this.summaryColorRow) {
          this.summaryColorRow.style.display = 'flex';
        }
        if (this.summaryColorName) {
          const titleEl = this.selectedColor.querySelector('.eyewear-configurator__color-name');
          this.summaryColorName.textContent = titleEl ? titleEl.textContent.trim() : (this.selectedColor.dataset.name || '');
        }
        const summaryColorPriceEl = document.getElementById('SummaryColorPrice');
        if (summaryColorPriceEl) {
          summaryColorPriceEl.textContent = this.formatMoney(this.lensPrice * 100).replace(/^\+\s*/, '');
        }
      } else {
        if (this.summaryColorRow) {
          this.summaryColorRow.style.display = 'none';
        }
      }
    } else {
      // Frame details
      if (this.summaryFramePrice) {
        this.summaryFramePrice.textContent = this.formatMoney(this.framePrice * 100);
      }

      const isPrescription = this.selectedLens && this.selectedLens.dataset.productHandle === 'prescription-lenses';

      // Lens details (Dynamic name assignment)
      if (this.summaryLensName && this.selectedLens) {
        if (isPrescription) {
          const option = this.state.prescriptionAdditionalOption;
          if (option === 'polarised') {
            this.summaryLensName.textContent = 'Prescription Polarised Lenses';
          } else if (option === 'personalised') {
            this.summaryLensName.textContent = 'Prescription Colour Lenses';
          } else {
            this.summaryLensName.textContent = 'Prescription Lenses';
          }
        } else {
          const titleEl = this.selectedLens.querySelector('.eyewear-configurator__lens-card-title');
          this.summaryLensName.textContent = titleEl ? titleEl.textContent.trim() : 'Lenses';
        }
      }

      // Lens price display (with Edit button for prescription)
      if (isPrescription) {
        if (this.summaryLensPrice) this.summaryLensPrice.style.display = 'none';
        if (this.summaryLensActionWrapper) this.summaryLensActionWrapper.style.display = '';
        if (this.summaryLensPricePersonalised) {
          let formattedPrice = this.formatMoney(this.lensPrice * 100);
          this.summaryLensPricePersonalised.textContent = formattedPrice.replace(/^\+\s*/, '');
        }
      } else {
        if (this.summaryLensActionWrapper) this.summaryLensActionWrapper.style.display = 'none';
        if (this.summaryLensPrice) {
          this.summaryLensPrice.style.display = '';
          if (this.lensPrice === 0) {
            this.summaryLensPrice.textContent = 'Included';
          } else {
            let formattedPrice = this.formatMoney(this.lensPrice * 100);
            this.summaryLensPrice.textContent = formattedPrice.replace(/^\+\s*/, '');
          }
        }
      }

      // Prescription details
      if (isPrescription) {
        if (this.summaryPrescriptionRow) {
          this.summaryPrescriptionRow.style.display = 'flex';
        }
        if (this.summaryPrescriptionName) {
          if (this.state.prescriptionMethod === 'upload_now') {
            this.summaryPrescriptionName.textContent = 'Uploaded Prescription';
          } else if (this.selectedPrescriptionOption) {
            const titleEl = this.selectedPrescriptionOption.querySelector('.eyewear-configurator__lens-card-title h3') || this.selectedPrescriptionOption.querySelector('.eyewear-configurator__lens-card-title');
            this.summaryPrescriptionName.textContent = titleEl ? titleEl.textContent.trim() : 'Send Later';
          } else {
            this.summaryPrescriptionName.textContent = 'Send Later';
          }
        }
      } else {
        if (this.summaryPrescriptionRow) {
          this.summaryPrescriptionRow.style.display = 'none';
        }
      }

      // Color details (Show/Hide and Name based on option card selection)
      const showColorRow = this.selectedColor && (!isPrescription || this.state.prescriptionAdditionalOption === 'polarised' || this.state.prescriptionAdditionalOption === 'personalised');

      if (showColorRow) {
        if (this.summaryColorRow) {
          this.summaryColorRow.style.display = 'flex';
        }
        if (this.summaryColorName && this.selectedColor) {
          const titleEl = this.selectedColor.querySelector('.eyewear-configurator__color-name');
          this.summaryColorName.textContent = titleEl ? titleEl.textContent.trim() : (this.selectedColor.dataset.name || '');
        }
        const summaryColorPriceEl = document.getElementById('SummaryColorPrice');
        if (summaryColorPriceEl) {
          summaryColorPriceEl.textContent = 'Complimentary';
        }
      } else {
        if (this.summaryColorRow) {
          this.summaryColorRow.style.display = 'none';
        }
      }
    }
  }

  /**
   * Format Money helper
   */
  formatMoney(cents) {
    let formatted = '';
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      const format = window.theme?.moneyFormat || Shopify.money_format || "${{amount}}";
      formatted = Shopify.formatMoney(cents, format);
    } else {
      // Fallback: extract currency symbol dynamically
      const symbol = this.getCurrencySymbol();
      const amount = (cents / 100).toFixed(2);
      formatted = `${symbol}${amount}`;
    }
    return formatted.replace(/\.00(?!\d)|,00(?!\d)/g, '');
  }

  /**
   * Extract currency symbol from footer total element
   */
  getCurrencySymbol() {
    const text = this.totalPriceEl ? this.totalPriceEl.textContent.trim() : '';
    const match = text.match(/^[^0-9]*/);
    return match && match[0] ? match[0] : '€';
  }

  /**
   * Equalise prescription option card heights (runtime — no hardcoded values).
   * Resets to natural height, measures tallest, applies to all.
   * Only affects [data-prescription-handle] cards inside PrescriptionSelectionPane.
   */
  equalizePrescriptionCardHeights() {
    // 1. Equalize cards inside PrescriptionSelectionPane
    const selectPaneCards = this.prescriptionOptionCards;
    if (selectPaneCards && selectPaneCards.length > 0) {
      selectPaneCards.forEach(c => { c.style.height = ''; });
      let maxH = 0;
      selectPaneCards.forEach(c => {
        const h = c.offsetHeight;
        if (h > maxH) maxH = h;
      });
      if (maxH > 0) {
        selectPaneCards.forEach(c => { c.style.height = maxH + 'px'; });
      }
    }

    // 2. Equalize cards inside PrescriptionAdditionalOptionsPane
    const additionalPaneCards = this.prescriptionAdditionalOptionCards;
    if (additionalPaneCards && additionalPaneCards.length > 0) {
      additionalPaneCards.forEach(c => { c.style.height = ''; });
      let maxH = 0;
      additionalPaneCards.forEach(c => {
        const h = c.offsetHeight;
        if (h > maxH) maxH = h;
      });
      if (maxH > 0) {
        additionalPaneCards.forEach(c => { c.style.height = maxH + 'px'; });
      }
    }
  }

  /**
   * Show Prescription Selection Pane
   */
  showPrescriptionPane(restoreSelection = false) {
    // Hide all step panes (same pattern as goToStep)
    this.stepPanes.forEach(pane => { pane.style.display = 'none'; });
    if (this.prescriptionOptionsPane) this.prescriptionOptionsPane.style.display = 'none';
    if (this.uploadPane) this.uploadPane.style.display = 'none';
    // Show prescription pane
    if (this.prescriptionPane) {
      this.prescriptionPane.style.display = 'flex';
      this.prescriptionPane.style.flexDirection = 'column';
    }
    // Show back button (override: currentStep is still 1)
    if (this.backBtn) this.backBtn.style.display = 'flex';
    // Hide step 1 Next button
    if (this.nextToStep2Btn) this.nextToStep2Btn.style.display = 'none';
    // Ensure ATC button hidden
    if (this.atcBtn) this.atcBtn.style.display = 'none';

    if (!restoreSelection && !this.editingBundleId) {
      // Reset prescription option selections
      this.prescriptionOptionCards.forEach(c => c.classList.remove('is-selected'));
      this.selectedPrescriptionOption = null;
      if (this.nextFromPrescriptionBtn) this.nextFromPrescriptionBtn.disabled = true;
    } else {
      // Restore previous selection
      if (this.selectedPrescriptionOption) {
        this.prescriptionOptionCards.forEach(c => {
          if (c === this.selectedPrescriptionOption) {
            c.classList.add('is-selected');
          } else {
            c.classList.remove('is-selected');
          }
        });
        if (this.nextFromPrescriptionBtn) this.nextFromPrescriptionBtn.disabled = false;
      }
    }

    // Reset lensPrice to base prescription price
    this.lensPrice = this.basePrescriptionPrice;

    // Update footer total to include prescription lens price
    if (this.totalPriceEl) {
      const totalCents = (this.framePrice + this.lensPrice) * 100;
      this.totalPriceEl.innerHTML = this.formatMoney(totalCents);
    }
    // Equalise card heights after pane is visible (offset is now measurable)
    this.equalizePrescriptionCardHeights();
  }

  /**
   * Hide Prescription Selection Pane and return to Step 1
   */
  hidePrescriptionPane() {
    if (this.prescriptionPane) this.prescriptionPane.style.display = 'none';
    if (this.prescriptionOptionsPane) this.prescriptionOptionsPane.style.display = 'none';
    if (this.uploadPane) this.uploadPane.style.display = 'none';
    // Reset prescription option selections
    this.prescriptionOptionCards.forEach(c => c.classList.remove('is-selected'));
    this.selectedPrescriptionOption = null;
    if (this.nextFromPrescriptionBtn) this.nextFromPrescriptionBtn.disabled = true;
    // Reset equalised card heights so natural height restores on next open
    this.prescriptionOptionCards.forEach(c => { c.style.height = ''; });
    // Return to Step 1
    this.goToStep(1);
    // Restore prescription lens card as selected in Step 1
    if (this.prescriptionLensCard) {
      this.prescriptionLensCard.classList.add('is-selected');
      if (this.nextToStep2Btn) this.nextToStep2Btn.disabled = false;
    }
  }

  /**
   * Show Prescription Additional Options Pane
   */
  showPrescriptionOptionsPane() {
    // Hide all step panes (same pattern as goToStep)
    this.stepPanes.forEach(pane => { pane.style.display = 'none'; });
    if (this.prescriptionPane) this.prescriptionPane.style.display = 'none';
    if (this.uploadPane) this.uploadPane.style.display = 'none';

    // Show options pane
    if (this.prescriptionOptionsPane) {
      this.prescriptionOptionsPane.style.display = 'flex';
      this.prescriptionOptionsPane.style.flexDirection = 'column';
    }

    // Show back button (override: currentStep is still 1)
    if (this.backBtn) this.backBtn.style.display = 'flex';
    // Hide step 1 Next button
    if (this.nextToStep2Btn) this.nextToStep2Btn.style.display = 'none';
    // Ensure ATC button hidden
    if (this.atcBtn) this.atcBtn.style.display = 'none';

    // Restore or reset prescription additional options selection
    if (this.state.prescriptionAdditionalOption) {
      let selectedOptionPrice = 0;
      this.prescriptionAdditionalOptionCards.forEach(c => {
        if (c.dataset.prescriptionOption === this.state.prescriptionAdditionalOption) {
          c.classList.add('is-selected');
          selectedOptionPrice = parseFloat(c.dataset.price || 0) / 100;
        } else {
          c.classList.remove('is-selected');
        }
      });
      this.lensPrice = this.basePrescriptionPrice + selectedOptionPrice;
      this.updateFooterTotal();
      if (this.nextFromPrescriptionOptionsBtn) {
        this.nextFromPrescriptionOptionsBtn.disabled = false;
      }
    } else {
      this.prescriptionAdditionalOptionCards.forEach(c => c.classList.remove('is-selected'));
      this.lensPrice = this.basePrescriptionPrice;
      this.updateFooterTotal();
      if (this.nextFromPrescriptionOptionsBtn) {
        this.nextFromPrescriptionOptionsBtn.disabled = true;
      }
    }

    // Equalise card heights after pane is visible
    this.equalizePrescriptionCardHeights();
  }

  /**
   * Hide Prescription Additional Options Pane and return to prescription pane
   */
  hidePrescriptionOptionsPane() {
    if (this.prescriptionOptionsPane) {
      this.prescriptionOptionsPane.style.display = 'none';
    }
    // Reset equalised card heights so natural height restores on next open
    this.prescriptionAdditionalOptionCards.forEach(c => { c.style.height = ''; });

    // Show Your Prescription Pane with restored selections or Upload Pane depending on choice
    if (this.state.prescriptionMethod === 'upload_now') {
      this.showPrescriptionUploadPane();
    } else {
      this.showPrescriptionPane(true);
    }
  }

  /**
   * Show Prescription Upload Pane (Phase 8.1)
   */
  showPrescriptionUploadPane() {
    this.stepPanes.forEach(pane => { pane.style.display = 'none'; });
    if (this.prescriptionPane) this.prescriptionPane.style.display = 'none';
    if (this.prescriptionOptionsPane) this.prescriptionOptionsPane.style.display = 'none';

    if (this.uploadPane) {
      this.uploadPane.style.display = 'flex';
      this.uploadPane.style.flexDirection = 'column';
    }

    if (this.backBtn) this.backBtn.style.display = 'flex';
    if (this.nextToStep2Btn) this.nextToStep2Btn.style.display = 'none';
    if (this.atcBtn) this.atcBtn.style.display = 'none';

    this.equalizePrescriptionCardHeights();
  }

  /**
   * Hide Prescription Upload Pane and return to prescription selection pane
   */
  hidePrescriptionUploadPane() {
    if (this.uploadPane) this.uploadPane.style.display = 'none';
    this.showPrescriptionPane(true);
  }

  /**
   * Initialize Prescription Upload events
   */
  initUploadEvents() {
    if (!this.uploadPane) return;

    // 1. Click upload zone to open file picker
    if (this.uploadZone && this.fileInput) {
      this.uploadZone.addEventListener('click', () => {
        this.fileInput.click();
      });

      // Prevent default drag & drop behaviors on upload zone
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        this.uploadZone.addEventListener(eventName, (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      });

      // Visual dragover states
      ['dragenter', 'dragover'].forEach(eventName => {
        this.uploadZone.addEventListener(eventName, () => {
          this.uploadZone.classList.add('dragover');
        });
      });

      ['dragleave', 'drop'].forEach(eventName => {
        this.uploadZone.addEventListener(eventName, () => {
          this.uploadZone.classList.remove('dragover');
        });
      });

      // Handle drop event
      this.uploadZone.addEventListener('drop', (e) => {
        const dt = e.dataTransfer;
        const files = dt.files;
        if (files && files.length > 0) {
          this.handleFileSelect(files[0]);
        }
      });
    }

    // 2. Prevent default drop behavior on window to avoid browser opening the file
    ['dragover', 'drop'].forEach(eventName => {
      window.addEventListener(eventName, (e) => {
        e.preventDefault();
      }, false);
    });

    // 3. File input change event
    if (this.fileInput) {
      this.fileInput.addEventListener('change', (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
          this.handleFileSelect(files[0]);
        }
      });
    }

    // 4. Remove selected file button
    if (this.removeFileBtn) {
      this.removeFileBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.resetUploadState();
      });
    }

    // 5. Validate button click
    if (this.validateUploadBtn) {
      this.validateUploadBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Safety safeguard check
        if (!this.uploadedFile) return;

        // Proceed to Additional Options pane
        this.showPrescriptionOptionsPane();
      });
    }

    // 6. Fallback Link ("Send my prescription later")
    if (this.uploadFallbackBtn) {
      this.uploadFallbackBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Reset upload state
        this.resetUploadState();

        // Select "Send later" card in selection pane
        this.prescriptionOptionCards.forEach(c => {
          if (c.dataset.prescriptionHandle === 'prescription-send-later') {
            c.classList.add('is-selected');
            this.selectedPrescriptionOption = c;
          } else {
            c.classList.remove('is-selected');
          }
        });

        // Set state properties
        this.state.prescriptionMethod = 'send_later';

        // Select prescription lens card selection (in step 1)
        if (this.prescriptionLensCard) {
          this.prescriptionLensCard.classList.add('is-selected');
          this.selectedLens = this.prescriptionLensCard;
          this.lensPrice = parseFloat(this.prescriptionLensCard.dataset.basePrice || 0) / 100;
        }

        // Navigate to additional options pane
        this.showPrescriptionOptionsPane();
      });
    }
  }

  /**
   * Validate selected or dropped file (Phase 8.1)
   */
  validateFile(file) {
    if (!file) return false;

    // MIME type check
    const allowedMimeTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    const hasValidMime = allowedMimeTypes.includes(file.type);

    // Extension check
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];
    const hasValidExt = allowedExtensions.includes(ext);

    if (!hasValidMime || !hasValidExt) {
      this.showUploadError('Invalid file type. Only PDF, PNG, JPG, or JPEG files are allowed.');
      return false;
    }

    // 10MB size limit check
    const maxSizeBytes = 10485760;
    if (file.size > maxSizeBytes) {
      this.showUploadError('File is too large. Maximum allowed size is 10MB.');
      return false;
    }

    return true;
  }

  /**
   * Process and show selected/dropped file
   */
  handleFileSelect(file) {
    this.resetUploadError();
    if (!file) return;

    if (!this.validateFile(file)) {
      this.uploadedFile = null;
      if (this.fileInput) this.fileInput.value = '';
      if (this.fileCard) this.fileCard.style.display = 'none';
      if (this.uploadZone) this.uploadZone.style.display = 'flex';
      if (this.validateUploadBtn) this.validateUploadBtn.disabled = true;
      return;
    }

    this.uploadedFile = file;
    this.state.prescriptionMethod = 'upload_now';

    if (this.uploadZone) this.uploadZone.style.display = 'none';
    if (this.fileCard) this.fileCard.style.display = 'flex';

    if (this.fileNameEl) {
      this.fileNameEl.textContent = file.name;
    }

    if (this.fileSizeEl) {
      this.fileSizeEl.textContent = this.formatBytes(file.size);
    }

    if (this.validateUploadBtn) {
      this.validateUploadBtn.disabled = false;
    }

    if (this.prescriptionLensCard) {
      this.prescriptionLensCard.classList.add('is-selected');
      this.selectedLens = this.prescriptionLensCard;
      this.lensPrice = parseFloat(this.prescriptionLensCard.dataset.basePrice || 0) / 100;
    }
    this.updateFooterTotal();
  }

  /**
   * Show error messages under upload area
   */
  showUploadError(msg) {
    if (this.uploadErrorEl) {
      this.uploadErrorEl.textContent = msg;
      this.uploadErrorEl.style.display = 'block';
    }
  }

  /**
   * Clear error message
   */
  resetUploadError() {
    if (this.uploadErrorEl) {
      this.uploadErrorEl.textContent = '';
      this.uploadErrorEl.style.display = 'none';
    }
  }

  /**
   * Helper to format bytes to human readable sizes
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Clear entire file upload state
   */
  resetUploadState() {
    this.uploadedFile = null;
    if (this.fileInput) {
      this.fileInput.value = '';
    }
    if (this.uploadZone) {
      this.uploadZone.style.display = 'flex';
      this.uploadZone.classList.remove('dragover');
    }
    if (this.fileCard) {
      this.fileCard.style.display = 'none';
    }
    if (this.fileNameEl) {
      this.fileNameEl.textContent = '';
    }
    if (this.fileSizeEl) {
      this.fileSizeEl.textContent = '';
    }
    this.resetUploadError();
    if (this.validateUploadBtn) {
      this.validateUploadBtn.disabled = true;
    }
  }

  /**
   * iOS Safari Body Scroll Lock
   */
  lockScroll() {
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';

    document.documentElement.classList.add('prevent-scroll');
  }

  /**
   * iOS Safari Body Scroll Unlock
   */
  unlockScroll() {
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');

    document.documentElement.classList.remove('prevent-scroll');

    window.scrollTo(0, this.scrollPosition);
  }

  /**
   * AJAX Cart Integration
   */
  addToCart() {
    if (this.atcBtn) {
      this.atcBtn.disabled = true;
      this.atcBtn.classList.add('m-spinner-loading');
    }

    // Fetch active frame variant ID
    let frameVariantId = null;
    const mainProductFormIdInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (mainProductFormIdInput) {
      frameVariantId = mainProductFormIdInput.value;
    }
    if (!frameVariantId) {
      const fallbackFrameInput = document.getElementById('EyewearConfiguratorFrameVariant');
      if (fallbackFrameInput) {
        frameVariantId = fallbackFrameInput.value;
      }
    }

    // Fetch lens variant ID
    const isPrescriptionLens = this.selectedLens && this.selectedLens.dataset.productHandle === 'prescription-lenses';
    let lensVariantId = null;
    if (isPrescriptionLens) {
      lensVariantId = this.selectedLens.dataset.variantId;
    } else {
      lensVariantId = this.selectedColor ? this.selectedColor.dataset.variantId : null;
    }

    // Capture in state as clean strings
    this.state.frameVariantId = frameVariantId ? String(frameVariantId).trim() : null;
    this.state.lensVariantId = lensVariantId ? String(lensVariantId).trim() : null;

    if (!this.state.frameVariantId || !this.state.lensVariantId) {
      const errorMsg = 'Frame or Lens variant ID is missing';
      console.error(errorMsg);
      if (window.MinimogTheme && window.MinimogTheme.Notification && typeof window.MinimogTheme.Notification.show === 'function') {
        window.MinimogTheme.Notification.show({
          target: document.body,
          method: 'appendChild',
          type: 'error',
          message: errorMsg,
          last: 3000,
          sticky: true
        });
      } else {
        alert(errorMsg);
      }

      if (this.atcBtn) {
        this.atcBtn.disabled = false;
        this.atcBtn.classList.remove('m-spinner-loading');
      }
      return;
    }

    // Generate or preserve Bundle ID in state
    if (this.editingBundleId) {
      this.state.bundleId = this.editingBundleId;
    } else {
      this.state.bundleId = Date.now().toString();
    }

    const isPrescription = isPrescriptionLens;
    const selectedHandle = this.selectedLens ? this.selectedLens.dataset.productHandle : '';
    const isPersonalised = selectedHandle === 'personalised-lens-colour';

    // Determine lens name based on flow
    let lensMainTitle;
    if (isPrescription) {
      const option = this.state.prescriptionAdditionalOption;
      if (option === 'polarised') {
        lensMainTitle = 'Prescription Polarised Lenses';
      } else if (option === 'personalised') {
        lensMainTitle = 'Prescription Colour Lenses';
      } else {
        lensMainTitle = 'Prescription Lenses';
      }
    } else {
      lensMainTitle = this.selectedLens ? (this.selectedLens.dataset.lensName || 'Polarised Lenses') : 'Lenses';
    }

    // Determine lens color
    const lensVariantTitle = this.selectedColor ? (this.selectedColor.dataset.variantTitle || this.selectedColor.dataset.name || '') : '';

    const childProperties = {
      '_bundle_id': this.state.bundleId,
      '_is_lens': 'true',
      '_lens_name': lensMainTitle,
      '_lens_color': lensVariantTitle,
      '_lens_type': selectedHandle,
      '_selected_color': lensVariantTitle
    };

    // Prescription-specific properties
    if (isPrescription) {
      childProperties['_prescription_method'] = this.state.prescriptionMethod || '';
      if (this.state.prescriptionMethod === 'send_later') {
        childProperties['Prescription Method'] = 'Send Later';
      } else if (this.state.prescriptionMethod === 'upload_now') {
        childProperties['_prescription_status'] = 'uploaded';
        if (this.uploadedFile && this.uploadedFile.name) {
          childProperties['_prescription_file_name'] = this.uploadedFile.name;
        }
      }
      const option = this.state.prescriptionAdditionalOption || 'none';
      childProperties['_additional_option'] = option;
      if (option === 'polarised') {
        childProperties['_flow_type'] = 'prescription_polarised';
      } else if (option === 'personalised') {
        childProperties['_flow_type'] = 'prescription_personalised';
      } else {
        childProperties['_flow_type'] = 'prescription';
      }
    } else if (isPersonalised) {
      childProperties['_flow_type'] = 'personalised';
      childProperties['_additional_option'] = 'none';
      childProperties['Complimentary'] = 'Standard lenses (€0)';
    } else {
      childProperties['_flow_type'] = 'polarised';
      childProperties['_additional_option'] = 'none';
      const titleEl = this.selectedColor ? this.selectedColor.querySelector('.eyewear-configurator__color-name') : null;
      const colorName = titleEl ? titleEl.textContent.trim() : (this.selectedColor ? (this.selectedColor.dataset.name || '') : '');
      if (colorName) {
        childProperties['Complimentary'] = `${colorName} (€0)`;
      }
    }

    // Payload
    const cartItems = [
      {
        id: parseInt(this.state.frameVariantId),
        quantity: 1,
        properties: {
          '_bundle_id': this.state.bundleId,
          '_is_main_frame': 'true',
          '_lens_added': 'Yes'
        }
      },
      {
        id: parseInt(this.state.lensVariantId),
        quantity: 1,
        parent_id: parseInt(this.state.frameVariantId),
        properties: childProperties
      }
    ];

    // For prescription + polarised/personalised: add color variant as 3rd item
    const prescriptionOption = this.state.prescriptionAdditionalOption;
    if (isPrescription && this.selectedColor && (prescriptionOption === 'polarised' || prescriptionOption === 'personalised')) {
      const colorVariantId = this.selectedColor.dataset.variantId;
      if (colorVariantId) {
        const colorProperties = {
          '_bundle_id': this.state.bundleId,
          '_is_color_addon': 'true',
          '_lens_color': lensVariantTitle,
          '_prescription_method': this.state.prescriptionMethod || 'send_later'
        };
        if (prescriptionOption === 'polarised') {
          colorProperties['_flow_type'] = 'prescription_polarised_color';
        } else {
          colorProperties['_flow_type'] = 'prescription_personalised_color';
        }
        if ((prescriptionOption === 'polarised' || prescriptionOption === 'personalised') && colorProperties) {
          const titleEl = this.selectedColor ? this.selectedColor.querySelector('.eyewear-configurator__color-name') : null;
          const colorName = titleEl ? titleEl.textContent.trim() : (this.selectedColor ? (this.selectedColor.dataset.name || '') : '');
          if (colorName) {
            colorProperties['Complimentary'] = `${colorName} (€0)`;
          }
        }
        cartItems.push({
          id: parseInt(colorVariantId),
          quantity: 1,
          parent_id: parseInt(this.state.frameVariantId),
          properties: colorProperties
        });
      }
    }

    const formData = { items: cartItems };

    const cartAddUrl = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart/add.js';

    let removePromise = Promise.resolve();
    if (this.editingBundleId) {
      const editingId = this.editingBundleId;
      removePromise = fetch((window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart.js')
        .then(response => response.json())
        .then(cart => {
          const updates = {};
          cart.items.forEach(item => {
            if (item.properties && item.properties._bundle_id === editingId) {
              updates[item.key] = 0;
            }
          });
          return fetch((window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ updates })
          });
        });
    }

    removePromise
    .then(() => {
      return fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(errData => {
          throw new Error(errData.description || errData.message || 'Network response was not ok');
        }).catch(() => {
          throw new Error('Network response was not ok');
        });
      }
      return response.json();
    })
    .then(data => {
      this.editingBundleId = null;
      // Close the configurator modal immediately
      this.closeModal();

      // Trigger multiple fallback events for Minimog theme cart drawer
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.body.dispatchEvent(new CustomEvent('cart:refresh'));
      document.body.dispatchEvent(new CustomEvent('ajaxProduct:added'));

      // Fallback for newer FoxThemes/Minimog versions
      if (window.MinimogTheme && window.MinimogTheme.Cart) {
        if (typeof window.MinimogTheme.Cart.getCart === 'function') {
          window.MinimogTheme.Cart.getCart();
        }
      }

      // Open cart drawer
      const cartDrawer = document.querySelector("m-cart-drawer");
      if (cartDrawer && window.MinimogSettings && window.MinimogSettings.enable_cart_drawer) {
        if (window.MinimogTheme && window.MinimogTheme.Notification && typeof window.MinimogTheme.Notification.show === 'function') {
          const cartItemsEl = cartDrawer.querySelector("m-cart-items");
          if (cartItemsEl) {
            window.MinimogTheme.Notification.show({
              target: cartItemsEl,
              method: "prepend",
              type: "success",
              message: window.MinimogStrings?.itemAdded || "Item added to cart",
              delay: 400,
            });
          }
        }
        cartDrawer.open();
      }
    })
    .catch(error => {
      console.error('Error adding configuration to cart:', error);
      const errorMsg = error.message || 'Error adding configuration to cart.';
      if (window.MinimogTheme && window.MinimogTheme.Notification && typeof window.MinimogTheme.Notification.show === 'function') {
        window.MinimogTheme.Notification.show({
          target: document.body,
          method: 'appendChild',
          type: 'error',
          message: errorMsg,
          last: 3000,
          sticky: true
        });
      } else {
        alert(errorMsg);
      }
    })
    .finally(() => {
      if (this.atcBtn) {
        this.atcBtn.disabled = false;
        this.atcBtn.classList.remove('m-spinner-loading');
      }
    });
  }

  /**
   * Accessibility Helper: Find Focusable Elements
   */
  getFocusableElements() {
    const selectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    return Array.from(this.modal.querySelectorAll(selectors)).filter(el => {
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  }

  /**
   * Accessibility Helper: Keydown Listener
   */
  handleKeyDown(e) {
    if (e.key === 'Escape') {
      this.closeModal();
      return;
    }

    if (e.key === 'Tab') {
      const focusable = this.getFocusableElements();
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    }
  }
}

// Instantiate the Class on DOM Load
document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('eyewear-configurator-modal')) {
    window.eyewearConfigurator = new EyewearConfigurator();
  }
});
